import { useState } from 'react';
import { useConnectorTools } from '@/lib/use-connectors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, Search, RefreshCw, Wrench, AlertCircle } from 'lucide-react';

interface ConnectorToolsDialogProps {
  connectorId: string;
  onClose: () => void;
}

export function ConnectorToolsDialog({ connectorId, onClose }: ConnectorToolsDialogProps) {
  const { tools, loading, error, discover } = useConnectorTools(connectorId);
  const [search, setSearch] = useState('');
  const [discovering, setDiscovering] = useState(false);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      await discover();
    } finally {
      setDiscovering(false);
    }
  };

  const filteredTools = tools.filter(tool =>
    !search ||
    tool.name.toLowerCase().includes(search.toLowerCase()) ||
    (tool.description && tool.description.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-lg border w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center size-8 rounded-lg bg-primary/10">
              <Wrench className="size-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold leading-tight">Connector Tools</h2>
              <p className="text-xs text-muted-foreground">{tools.length} tool{tools.length !== 1 ? 's' : ''} available</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDiscover}
              disabled={discovering}
              className="h-7 px-2 text-xs"
            >
              {discovering ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <RefreshCw className="size-3.5" />
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* Search */}
        {tools.length > 5 && (
          <div className="px-4 py-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tools..."
                className="pl-8 h-8 text-xs"
              />
            </div>
          </div>
        )}

        {/* Tools list */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-3">
            {error ? (
              <div className="flex items-start gap-2 bg-destructive/10 text-destructive text-xs p-3 rounded-md border border-destructive/20">
                <AlertCircle className="size-3.5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium mb-0.5">Failed to load tools</p>
                  <p className="text-[11px] opacity-80">{error}</p>
                </div>
              </div>
            ) : loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="size-5 text-primary animate-spin mb-2" />
                <p className="text-xs text-muted-foreground">Loading tools...</p>
              </div>
            ) : filteredTools.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="flex items-center justify-center size-10 rounded-xl bg-muted mb-3">
                  <Wrench className="size-5 text-muted-foreground" />
                </div>
                <p className="text-xs text-muted-foreground">
                  {tools.length === 0
                    ? 'No tools found. Try discovering tools.'
                    : 'No tools match your search.'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredTools.map(tool => (
                  <div
                    key={tool.id}
                    className="rounded-md border bg-card p-2.5 hover:bg-accent/40 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-mono font-medium truncate">{tool.name}</span>
                      {(tool.isEnabled === false || tool.isEnabled === 0) && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">disabled</Badge>
                      )}
                    </div>
                    {tool.description && (
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mb-1">
                        {tool.description}
                      </p>
                    )}
                    {tool.inputSchema && Object.keys((tool.inputSchema as any).properties || {}).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {Object.keys((tool.inputSchema as any).properties || {}).map(param => (
                          <Badge key={param} variant="secondary" className="text-[10px] px-1.5 py-0 font-mono">
                            {param}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3 border-t bg-muted/30">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
